import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import {
  Phone, ChartLine, FlowArrow, Microphone, ShieldCheck, Headset,
  ArrowUpRight, ArrowRight, Globe, Lightning, Users, CheckCircle,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-[var(--text-primary)]" data-testid="landing-page">
      {/* Nav */}
      <header className="border-b border-[var(--border)]">
        <div className="max-w-[1320px] mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center" data-testid="brand-logo">
            <Logo size={44} />
          </div>
          <nav className="hidden md:flex items-center gap-8 text-[14px] font-medium text-[var(--text-secondary)]">
            <a href="#features" className="hover:text-[var(--text-primary)]">Product</a>
            <a href="#pricing" className="hover:text-[var(--text-primary)]">Pricing</a>
            <a href="#integrations" className="hover:text-[var(--text-primary)]">Integrations</a>
            <Link to="/download" className="hover:text-[var(--text-primary)]">Download</Link>
            <Link to="/developers" className="hover:text-[var(--text-primary)]" data-testid="nav-developers">Developers</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login?from=marketing" className="text-[14px] font-semibold" data-testid="nav-login">Log in</Link>
            <Link to="/start/business" className="btn-primary text-[14px]" data-testid="nav-signup">
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-[var(--border)] relative">
        <div className="max-w-[1320px] mx-auto px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-[var(--border)] bg-[var(--bg-sidebar)] mb-6">
              <span className="w-1.5 h-1.5 bg-[var(--success)] rounded-full" />
              <span className="font-mono text-[11px] tracking-widest">HELLO BY FLOW 2.0 · LIVE</span>
            </div>
            <h1 className="font-display font-black text-[72px] leading-[0.95] tracking-tight">
              The cloud<br/>
              <span className="text-[var(--accent)]">phone system</span><br/>
              built for teams<br/>
              that ship.
            </h1>
            <p className="text-[17px] text-[var(--text-secondary)] mt-7 max-w-xl leading-relaxed">
              Virtual numbers in 90+ countries. Click-to-call from your browser, smart IVR routing,
              real-time analytics, and recorded conversations — all in one workspace.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/start/business" className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta-primary">
                Start free trial <ArrowRight size={16} />
              </Link>
              <Link to="/download" className="btn-ghost inline-flex items-center gap-2" data-testid="hero-cta-download">
                Download desktop app <ArrowUpRight size={16} />
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-[13px] text-[var(--text-secondary)]">
              <div className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[var(--success)]" /> No credit card</div>
              <div className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[var(--success)]" /> Setup in 2 min</div>
              <div className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[var(--success)]" /> 14-day free</div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            {/* Mock app preview */}
            <div className="border border-[var(--border)] bg-white">
              <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-2 bg-[var(--bg-sidebar)]">
                <div className="w-2 h-2 rounded-full bg-[#EF4444]" />
                <div className="w-2 h-2 rounded-full bg-[#F59E0B]" />
                <div className="w-2 h-2 rounded-full bg-[#10B981]" />
                <span className="ml-3 font-mono text-[11px] text-[var(--text-muted)]">hellobyflow.com/dashboard</span>
              </div>
              <div className="p-6 bg-[var(--bg-dark)] text-white">
                <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">ACTIVE CALL · OUTBOUND</div>
                <div className="mt-3 font-display font-bold text-[22px]">Aiko Tanaka</div>
                <div className="font-mono text-[13px] text-[#94A3B8]">+81 90-1234-5678</div>
                <div className="font-mono text-[48px] mt-5 tracking-wide">04:21</div>
                <div className="flex items-end h-8 mt-3 gap-0.5">
                  {[40, 65, 30, 80, 50, 95, 35, 70, 55, 88, 42, 60, 75, 48, 90, 33, 70].map((h, i) => (
                    <div key={`wf-hero-${i}-${h}`} className="w-1.5 bg-[var(--success)]" style={{ height: `${h}%` }} />
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-2 mt-6">
                  <div className="border border-[#1E293B] py-3 flex flex-col items-center text-[10px] tracking-wide text-[#94A3B8]">
                    <Microphone size={16} /> MUTE
                  </div>
                  <div className="border border-[#1E293B] py-3 flex flex-col items-center text-[10px] tracking-wide text-[#94A3B8]">
                    <FlowArrow size={16} /> TRANSFER
                  </div>
                  <div className="bg-[var(--danger)] py-3 flex flex-col items-center text-[10px] tracking-wide text-white font-semibold">
                    END CALL
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 border border-[var(--border)] bg-white p-4 hidden lg:block">
              <div className="font-mono text-[10px] tracking-widest text-[var(--text-muted)]">CALLS TODAY</div>
              <div className="font-display font-extrabold text-[32px] mt-1">2,847</div>
              <div className="font-mono text-[11px] text-[var(--success)]">↑ 23% vs yesterday</div>
            </div>
          </div>
        </div>
      </section>

      {/* Logos marquee */}
      <section className="border-b border-[var(--border)] py-8 overflow-hidden">
        <div className="font-mono text-[11px] tracking-widest text-[var(--text-muted)] text-center mb-6">
          TRUSTED BY 4,200+ SALES & SUPPORT TEAMS
        </div>
        <div className="overflow-hidden">
          <div className="flex marquee gap-16 whitespace-nowrap font-display font-bold text-[24px] text-[var(--text-muted)] opacity-70">
            {["Nakamura Trading","Cobalt Labs","Indus Fintech","Nordic Flow","Porto Design","King Ventures","Seoul Bio","Brandt GmbH"].concat(["Nakamura Trading","Cobalt Labs","Indus Fintech","Nordic Flow","Porto Design","King Ventures","Seoul Bio","Brandt GmbH"]).map((n, i) => (
              <span key={`logo-${i}-${n}`}>{n}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-b border-[var(--border)]">
        <div className="max-w-[1320px] mx-auto px-8 py-20">
          <div className="grid grid-cols-12 gap-8 items-end mb-14">
            <div className="col-span-12 lg:col-span-7">
              <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ PLATFORM</div>
              <h2 className="font-display font-black text-[52px] leading-[1.0]">
                Everything your team needs<br/>to talk to customers.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 text-[15px] text-[var(--text-secondary)] leading-relaxed">
              One workspace replaces your VoIP provider, call recording vendor, IVR builder,
              and analytics stack. Built on Twilio-class infrastructure.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border border-[var(--border)]">
            {FEATURES.map((f) => (
              <Feature key={f.title} {...f} />
            ))}
          </div>
        </div>
      </section>

      {/* Cost section removed */}

      {/* Why Hello by Flow */}
      <section className="border-b border-[var(--border)]">
        <div className="max-w-[1320px] mx-auto px-8 py-20">
          <div className="grid grid-cols-12 gap-8 items-end mb-14">
            <div className="col-span-12 lg:col-span-7">
              <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ WHY HELLO BY FLOW</div>
              <h2 className="font-display font-black text-[52px] leading-[1.0]">
                One platform.<br/>Twelve tools replaced.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 text-[15px] text-[var(--text-secondary)] leading-relaxed">
              Teams using Hello by Flow stop juggling Twilio + a recording vendor + an analytics tool + an IVR builder + a softphone + a CRM-sync widget. Everything in one workspace, billed per seat.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {WHY.map((w) => (
              <div key={w.title} className="border-l-2 border-[var(--accent)] pl-5 py-2">
                <div className="font-mono text-[11px] tracking-widest text-[var(--accent)]">{w.tag}</div>
                <div className="font-display font-extrabold text-[24px] mt-2 leading-tight">{w.title}</div>
                <div className="text-[14px] text-[var(--text-secondary)] mt-2 leading-relaxed">{w.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats band */}
      <section className="border-b border-[var(--border)] bg-[var(--bg-dark)] text-white">
        <div className="max-w-[1320px] mx-auto px-8 py-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1E293B]">
          {[
            ["4,200+", "TEAMS WORLDWIDE"],
            ["98ms", "P95 CALL LATENCY"],
            ["99.99%", "UPTIME SLA"],
            ["90+", "COUNTRIES COVERED"],
          ].map(([v, l]) => (
            <div key={l} className="bg-[var(--bg-dark)] p-8 text-center">
              <div className="font-display font-black text-[52px] leading-none">{v}</div>
              <div className="font-mono text-[10px] text-[#94A3B8] tracking-widest mt-2">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison */}
      <section className="border-b border-[var(--border)]">
        <div className="max-w-[1320px] mx-auto px-8 py-20">
          <div className="text-center mb-12">
            <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ THE DIFFERENCE</div>
            <h2 className="font-display font-black text-[52px] leading-[1.0]">How Hello by Flow stacks up.</h2>
            <p className="text-[14px] text-[var(--text-secondary)] mt-4 max-w-2xl mx-auto">
              We benchmarked against the leading alternatives. Here's what makes Hello by Flow different.
            </p>
          </div>
          <div className="border border-[var(--border)] overflow-x-auto">
            <table className="tbl w-full" data-testid="comparison-table">
              <thead>
                <tr>
                  <th>Capability</th>
                  <th className="text-center font-bold text-[var(--accent)]">HELLO BY FLOW</th>
                  <th className="text-center">Twilio</th>
                  <th className="text-center">CallHippo</th>
                  <th className="text-center">Telecmi</th>
                  <th className="text-center">Phound</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row) => (
                  <tr key={row[0]}>
                    <td className="font-semibold">{row[0]}</td>
                    {row.slice(1).map((c, j) => (
                      <td key={`${row[0]}-c${j}`} className={`text-center ${j === 0 ? "bg-[var(--bg-sidebar)]" : ""}`}>
                        {c === true ? <span className="text-[var(--success)] font-bold">✓</span>
                          : c === false ? <span className="text-[var(--text-muted)]">—</span>
                          : <span className="font-mono text-[12px]">{c}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-b border-[var(--border)]">
        <div className="max-w-[1320px] mx-auto px-8 py-20">
          <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ TEAMS WHO SHIPPED FASTER</div>
          <h2 className="font-display font-black text-[44px] leading-[1.0] mb-12">Loved by sales & support teams.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border)]">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-white p-7">
                <div className="text-[16px] leading-relaxed">"{t.quote}"</div>
                <div className="mt-6 flex items-center gap-3 pt-4 border-t border-[var(--border)]">
                  <div className="w-10 h-10 bg-[var(--bg-sidebar)] border border-[var(--border)] flex items-center justify-center font-display font-bold text-[var(--accent)]">{t.initials}</div>
                  <div>
                    <div className="font-semibold text-[14px]">{t.name}</div>
                    <div className="font-mono text-[11px] text-[var(--text-muted)]">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Desktop app band */}
      <section className="border-b border-[var(--border)] bg-[var(--bg-sidebar)]">
        <div className="max-w-[1320px] mx-auto px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ DESKTOP APP · v1.0</div>
            <h2 className="font-display font-black text-[52px] leading-[1.0]">
              In your dock,<br/>not your browser tab.
            </h2>
            <p className="text-[15px] text-[var(--text-secondary)] mt-5 max-w-md leading-relaxed">
              Crisper audio, OS-level push for inbound calls, and a window your team won't accidentally close.
              The Hello by Flow desktop app for macOS, Windows, and Linux — same workspace, native feel.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/download" className="btn-primary inline-flex items-center gap-2" data-testid="landing-download-cta">
                <ArrowRight size={16} /> Get the desktop app
              </Link>
              <div className="flex items-center gap-4 text-[12px] text-[var(--text-secondary)]">
                <span className="inline-flex items-center gap-1.5"><span className="w-1 h-1 bg-[var(--text-muted)] rounded-full" /> macOS</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-1 h-1 bg-[var(--text-muted)] rounded-full" /> Windows</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-1 h-1 bg-[var(--text-muted)] rounded-full" /> Linux</span>
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="border border-[var(--border)] bg-white shadow-2xl">
              <div className="px-3 py-2 border-b border-[var(--border)] flex items-center gap-1.5 bg-[var(--bg-sidebar)]">
                <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#10B981]" />
                <span className="ml-3 font-mono text-[10px] text-[var(--text-muted)]">HELLO BY FLOW · DESKTOP</span>
              </div>
              <div className="p-6">
                <div className="font-mono text-[10px] tracking-widest text-[var(--accent)]">ACTIVE CALL · 03:42</div>
                <div className="font-display font-bold text-[22px] mt-2">Hana Park</div>
                <div className="font-mono text-[12px] text-[var(--text-muted)]">+82 10-9876-5432</div>
                <div className="flex items-end h-10 mt-4 gap-0.5">
                  {[55, 80, 35, 90, 50, 75, 45, 88, 32, 68, 78, 42, 90, 55, 70].map((h, i) => (
                    <div key={`wf-cta-${i}-${h}`} className="w-1.5 bg-[var(--accent)]" style={{ height: `${h}%` }} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing — dynamically sourced from Stripe */}
      <StripePricingSection />

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-[1320px] mx-auto px-8 text-center">
          <h2 className="font-display font-black text-[64px] leading-[1.0]">
            Stop juggling tools.<br/>
            <span className="text-[var(--accent)]">Start a call.</span>
          </h2>
          <Link to="/start/business" className="btn-primary inline-flex items-center gap-2 mt-8 text-[15px]" data-testid="footer-cta">
            Get started — it's free <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-[1320px] mx-auto px-8 flex flex-wrap items-center justify-between gap-4">
          <div className="font-mono text-[11px] tracking-widest text-[var(--text-muted)]">
            © 2026 HELLO BY FLOW · BUILT FOR TEAMS THAT TALK
          </div>
          <div className="flex gap-6 text-[12px] text-[var(--text-secondary)]">
            <Link to="/legal/privacy">Privacy</Link>
            <Link to="/legal/terms">Terms</Link>
            <Link to="/legal/aup">Acceptable Use</Link>
            <Link to="/legal/compliance">Telecom Compliance</Link>
            <Link to="/legal/cookies">Cookies</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

const FEATURES = [
  { icon: Phone, title: "Click-to-Call", desc: "Dial any contact straight from your CRM with a browser-based softphone." },
  { icon: Globe, title: "Virtual Numbers", desc: "Local, toll-free, and mobile numbers across 90+ countries — provisioned instantly." },
  { icon: FlowArrow, title: "Visual IVR Builder", desc: "Drag-and-drop call flows, business hours, menus and intelligent routing." },
  { icon: Microphone, title: "Call Recording", desc: "Cloud recording with searchable transcripts and waveform playback." },
  { icon: ChartLine, title: "Live Analytics", desc: "Real-time dashboards for call volume, missed-call rate, and agent performance." },
  { icon: Headset, title: "Team Inbox", desc: "Shared SMS inbox, agent assignment, and tagging — built for teams, not solos." },
  { icon: Lightning, title: "Power Dialer", desc: "Auto-dial through lists at 3× pace — no manual dialing between calls." },
  { icon: Users, title: "CRM Integrations", desc: "Sync calls, contacts and recordings with HubSpot, Salesforce, and Zoho." },
  { icon: ShieldCheck, title: "Enterprise-Grade", desc: "End-to-end encryption, SOC 2 Type II, GDPR & HIPAA-ready infrastructure." },
];

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="p-7 border-r border-b border-[var(--border)] hover:bg-[var(--bg-sidebar)] transition-colors">
      <Icon size={24} weight="duotone" className="text-[var(--accent)]" />
      <div className="font-display font-bold text-[20px] mt-4">{title}</div>
      <div className="text-[14px] text-[var(--text-secondary)] mt-2 leading-relaxed">{desc}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pricing section — sourced from Stripe via /api/public/pricing.
// Stripe is the single source of truth. If Stripe is unreachable, the
// backend falls back to billing_config so this component always renders.
// ---------------------------------------------------------------------------
function StripePricingSection() {
  const [state, setState] = useState({ status: "loading", data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/pricing?currency=usd`, {
          credentials: "omit",
          headers: { "Accept": "application/json" },
        });
        if (!res.ok) throw new Error(`Pricing API responded ${res.status}`);
        const data = await res.json();
        if (!cancelled) setState({ status: "ok", data, error: null });
      } catch (e) {
        if (!cancelled) setState({ status: "error", data: null, error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const products = state.data?.products || [];
  const isLoading = state.status === "loading";
  const isFallback = state.data?.source === "fallback";
  const priceCount = products.length;

  return (
    <section id="pricing" className="border-b border-[var(--border)]" data-testid="pricing-section">
      <div className="max-w-[1320px] mx-auto px-8 py-20">
        <div className="text-center mb-14">
          <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ PRICING</div>
          <h2 className="font-display font-black text-[52px] leading-[1.0]">Simple, transparent pricing.</h2>
          <p className="mt-4 text-[15px] text-[var(--text-secondary)]">
            Live from our billing system — the price you see here is the price you pay.
          </p>
        </div>

        {state.status === "error" && (
          <div className="max-w-2xl mx-auto text-center text-[14px] text-red-800 bg-red-50 border border-red-200 rounded p-4"
               data-testid="pricing-error">
            We couldn&apos;t reach the pricing service. Refresh the page, or <Link to="/start/business" className="underline">start a trial</Link> — pricing is confirmed at checkout.
          </div>
        )}

        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-3 border border-[var(--border)]" data-testid="pricing-skeleton">
            {[0, 1, 2].map((i) => (
              <div key={i} className="p-8 border-r border-[var(--border)] last:border-r-0 animate-pulse">
                <div className="h-6 w-32 bg-slate-200 rounded" />
                <div className="mt-6 h-14 w-24 bg-slate-200 rounded" />
                <div className="mt-6 space-y-2.5">
                  {[0, 1, 2, 3, 4].map((k) => <div key={k} className="h-4 w-full bg-slate-100 rounded" />)}
                </div>
                <div className="mt-7 h-10 w-full bg-slate-200 rounded" />
              </div>
            ))}
          </div>
        )}

        {state.status === "ok" && priceCount > 0 && (
          <div className={`grid grid-cols-1 ${priceCount >= 3 ? "md:grid-cols-3" : "md:grid-cols-2 max-w-4xl mx-auto"} border border-[var(--border)]`}>
            {products.map((p) => (
              <StripePlanCard key={p.plan_key} plan={p} />
            ))}
          </div>
        )}

        {state.status === "ok" && isFallback && (
          <div className="mt-6 text-center text-[11px] font-mono uppercase tracking-widest text-slate-400"
               data-testid="pricing-source-fallback">
            preview mode · pricing not yet live
          </div>
        )}
        {state.status === "ok" && !isFallback && (
          <div className="mt-6 text-center" data-testid="pricing-source-stripe">
            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-400">
              pricing live from Stripe · {state.data.currency?.toUpperCase()} · {state.data.live_mode ? "live mode" : "test mode"}
            </div>
            {state.data.stripe_account_id && (
              <div className="mt-1 text-[10px] font-mono text-slate-300" data-testid="pricing-source-account">
                account {state.data.stripe_account_id} · key from {state.data.stripe_key_source}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function StripePlanCard({ plan }) {
  const {
    plan_key, name, features, popular, contact_sales, billing_model, price,
  } = plan;
  const unit = price?.unit_label || (billing_model === "per_seat" ? "user" : null);
  const interval = price?.interval === "year" ? "yr" : "mo";
  const priceDisplay = price?.display;
  const ctaLabel = contact_sales ? "Talk to sales" : `Choose ${name.replace(/^Hello by Flow[\s\u2013\-]*/, "")}`;
  // Route every paid CTA into the gated Signup-Intent funnel (BusinessFunnel).
  // The funnel enforces KYC-before-payment + payment-before-activation and
  // provisions the correct number of Communication Seats.
  let ctaHref;
  if (contact_sales) {
    ctaHref = "/enterprise-inquiry";
  } else if (plan_key === "individual") {
    ctaHref = "/start/business?type=individual&plan=individual";
  } else {
    ctaHref = `/start/business?plan=${encodeURIComponent(plan_key)}`;
  }

  return (
    <div
      className={`p-8 border-r border-[var(--border)] last:border-r-0 ${popular ? "bg-[var(--bg-sidebar)]" : ""}`}
      data-testid={`pricing-card-${plan_key}`}
    >
      {popular && (
        <div className="font-mono text-[10px] tracking-widest text-[var(--accent)] mb-2" data-testid={`pricing-badge-${plan_key}`}>
          MOST POPULAR
        </div>
      )}
      <div className="font-display font-extrabold text-[22px]" data-testid={`pricing-name-${plan_key}`}>
        {name}
      </div>
      <div className="mt-4 flex items-baseline gap-1" data-testid={`pricing-amount-${plan_key}`}>
        {contact_sales ? (
          <span className="font-display font-black text-[44px]">Custom</span>
        ) : priceDisplay ? (
          <>
            <span className="font-display font-black text-[52px]">{priceDisplay}</span>
            <span className="text-[var(--text-secondary)] text-[14px]">
              {unit ? `/${unit}/${interval}` : `/${interval}`}
            </span>
          </>
        ) : (
          <span className="font-display font-bold text-[24px] text-slate-400">Price unavailable</span>
        )}
      </div>
      {plan.tagline && (
        <div className="text-[13px] text-[var(--text-muted)]" data-testid={`pricing-tagline-${plan_key}`}>{plan.tagline}</div>
      )}
      <ul className="mt-6 space-y-2.5">
        {(features || []).map((f, idx) => (
          <li key={`${plan_key}-f-${idx}`} className="flex items-start gap-2 text-[14px]">
            <CheckCircle size={16} weight="fill" className="text-[var(--success)] mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {contact_sales ? (
        <Link
          to={ctaHref}
          className="mt-7 block text-center py-3 font-semibold btn-ghost"
          data-testid={`pricing-cta-${plan_key}`}
        >
          {ctaLabel}
        </Link>
      ) : (
        <Link
          to={ctaHref}
          className={`mt-7 block text-center py-3 font-semibold ${popular ? "btn-primary" : "btn-ghost"}`}
          data-testid={`pricing-cta-${plan_key}`}
        >
          {ctaLabel}
        </Link>
      )}
    </div>
  );
}

const WHY = [
  { tag: "01 · UNIFIED", title: "Everything in one workspace", desc: "Stop bouncing between Twilio + Asterisk + a CRM widget + a recording vendor + an analytics tool. Hello by Flow ships them as one product." },
  { tag: "02 · INSTANT", title: "Numbers in 90 seconds", desc: "Provision local, toll-free, or mobile numbers across 90+ countries from inside the dashboard. No SIP trunks, no carrier paperwork." },
  { tag: "03 · TRANSPARENT", title: "Flat per-seat pricing", desc: "No overage charges, no SMS markup, no per-minute surprises. One flat monthly rate per user — that's it." },
  { tag: "04 · OPEN", title: "Built on Twilio-class infra", desc: "We use the same carrier-grade backbone as the enterprise CPaaS — without making you write the integration." },
  { tag: "05 · FAST", title: "98ms median call setup", desc: "Calls feel snappy because they are. Our edge POPs put a server within 20ms of every customer in 30 markets." },
  { tag: "06 · COMPLIANT", title: "SOC 2 + GDPR + HIPAA", desc: "Encrypted recordings, regional data residency, audit logs, BAA available. Compliance is shipped, not promised." },
];

const COMPARISON = [
  ["Click-to-call from browser", true, false, true, true, true],
  ["Virtual numbers in 90+ countries", true, true, true, false, false],
  ["Built-in visual IVR builder", true, false, true, true, false],
  ["Call recording included", true, "$0.025/min", true, true, "Add-on"],
  ["Team SMS inbox", true, true, false, false, true],
  ["Real-time analytics", true, "Add-on", true, true, false],
  ["Per-agent performance leaderboard", true, false, false, false, false],
  ["Native CRM sync (HubSpot/Salesforce)", true, "Via Zapier", true, false, false],
  ["Multi-tenancy / agency reseller mode", true, false, false, false, false],
  ["Flat per-seat pricing (no per-minute)", true, false, true, true, true],
];

const TESTIMONIALS = [
  { quote: "We migrated 28 agents from CallHippo + HubSpot in a weekend. Hello by Flow just replaced both. Our answer rate went from 71% to 89% in the first month.", name: "Sofia Almeida", role: "Head of Sales · Porto Design", initials: "SA" },
  { quote: "The IVR builder alone saved us six weeks. We used to need a $4k/mo telephony consultant. Now my ops manager builds flows in an afternoon.", name: "Marcus King", role: "COO · King Ventures", initials: "MK" },
  { quote: "I'm a solo founder doing 80 cold calls a day. Hello by Flow gives me a real business number, recordings I can review, and analytics — at a flat monthly rate.", name: "Diego Ramirez", role: "Founder · Cobalt Labs", initials: "DR" },
];
